import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import {
  collectContext,
  createOpId,
  MAX_DESCRIPTION_LENGTH,
  MAX_IMAGES,
  MAX_VIDEO_BYTES,
  MIN_DESCRIPTION_LENGTH,
  MAX_VIDEO_SECONDS,
  readVideoDurationSeconds,
  resizeImageForUpload,
  SEVERITIES,
  uploadBugReport,
  validateDraft,
  type BugReportFieldError,
  type BugReportSeverity,
  type UploadBugReportError,
  type UploadBugReportSuccess,
} from './bugReportCore';

const SALE_API_BASE_URL = (import.meta.env.VITE_SALE_API_URL ?? 'http://localhost:8003') as string;

interface BugReportSheetProps {
  open: boolean;
  onClose: () => void;
  token: string;
  isOnline: boolean;
}

interface ImageAttachment {
  file: File;
  previewUrl: string;
}

// review F1#10 — même token + même forme que `NewReservationSheet.tsx` /
// `ReservationsPanel.tsx` (`var(--w-danger)`, `font-sans`), pas le
// `text-red-600 font-mono` des feuilles plus anciennes de ce dossier.
const ERROR_TEXT_CLASSES = 'font-sans text-base font-semibold';
const ERROR_TEXT_STYLE = { color: 'var(--w-danger)' } as const;

function serverErrorKey(error: UploadBugReportError): string {
  switch (error.kind) {
    case 'tooLarge':
      return 'waiter.bugReport.errors.tooLarge';
    case 'throttled':
      return 'waiter.bugReport.errors.throttled';
    case 'invalid':
      return 'waiter.bugReport.errors.invalid';
    default:
      return 'waiter.bugReport.errors.generic';
  }
}

/**
 * SC-1082 — « Signaler un bug » : la feuille ouverte par `BugReportRow`
 * depuis l'identité du serveur. Contrat de fil, contexte auto-collecté et
 * envoi vivent dans `bugReportCore.ts` ; ce composant n'est que la mise en
 * forme + l'état local du brouillon.
 *
 * Même convention que `NoteCreateModals` : le corps n'est monté que quand la
 * feuille est ouverte (`{open && <Body/>}`), ce qui remet le brouillon à
 * zéro pour la prochaine ouverture sans code de reset dédié.
 */
export default function BugReportSheet({ open, onClose, token, isOnline }: BugReportSheetProps) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('waiter.bugReport.title')} instant dismissOnBackdrop={false}>
      {open && <BugReportSheetBody token={token} isOnline={isOnline} onClose={onClose} />}
    </Modal>
  );
}

interface BodyProps {
  token: string;
  isOnline: boolean;
  onClose: () => void;
}

function BugReportSheetBody({ token, isOnline, onClose }: BodyProps) {
  const { t, i18n } = useTranslation();
  const [opId] = useState(createOpId);
  const [severity, setSeverity] = useState<BugReportSeverity | null>(null);
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const [video, setVideo] = useState<File | null>(null);
  const [videoDurationSeconds, setVideoDurationSeconds] = useState<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<BugReportFieldError[]>([]);
  const [serverError, setServerError] = useState<UploadBugReportError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<UploadBugReportSuccess | null>(null);
  // review F1#5 — le × fermait sans prévenir et perdait texte + pièces
  // jointes ; un brouillon "sale" (quoi que ce soit de saisi) passe d'abord
  // par cette confirmation, dans la feuille elle-même (même bascule que
  // l'état de succès ci-dessous) plutôt qu'une seconde modale empilée.
  const [confirmingClose, setConfirmingClose] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Les URL d'objet des vignettes photo doivent être révoquées à la
  // fermeture, quel que soit l'état des images à ce moment — une ref plutôt
  // que la variable `images` elle-même, qui serait figée par le tableau de
  // dépendances vide du useEffect ci-dessous.
  const imagesRef = useRef<ImageAttachment[]>(images);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, []);

  async function handlePhotoChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) {
      setFieldErrors(['tooManyImages']);
      return;
    }
    const resized = await Promise.all(files.slice(0, room).map((file) => resizeImageForUpload(file)));
    setImages((prev) => [...prev, ...resized.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }))]);
  }

  function removeImage(index: number) {
    setImages((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function handleVideoChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) {
      setFieldErrors(['videoTooLarge']);
      return;
    }
    const duration = await readVideoDurationSeconds(file);
    if (duration != null && duration > MAX_VIDEO_SECONDS) {
      setFieldErrors(['videoTooLong']);
      return;
    }
    setVideo(file);
    setVideoDurationSeconds(duration);
  }

  function removeVideo() {
    setVideo(null);
    setVideoDurationSeconds(null);
  }

  function isDraftDirty(): boolean {
    return !!severity || description.trim().length > 0 || images.length > 0 || !!video;
  }

  function requestClose() {
    if (isDraftDirty()) {
      setConfirmingClose(true);
    } else {
      onClose();
    }
  }

  async function handleSubmit() {
    const draft = { severity, description, images: images.map((i) => i.file), video };
    const errors = validateDraft(draft);
    setFieldErrors(errors);
    setServerError(null);
    if (errors.length > 0 || !severity) return;

    setSubmitting(true);
    setProgress(0);
    const outcome = await uploadBugReport({
      baseUrl: SALE_API_BASE_URL,
      token,
      opId,
      severity,
      description,
      context: collectContext(i18n.language),
      images: draft.images,
      video,
      onProgress: setProgress,
    });
    setSubmitting(false);
    if (outcome.ok) {
      setResult(outcome.data);
    } else {
      setServerError(outcome.error);
    }
  }

  if (result) {
    return (
      <div data-testid="bug-report-success" className="p-5 flex flex-col gap-4">
        <p className="font-sans text-lg font-bold text-ink">{t('waiter.bugReport.successThanks')}</p>
        <p className="font-sans text-base text-ink">
          {t('waiter.bugReport.successReference', { reference: result.reference })}
        </p>
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="bug-report-success-close"
            onClick={onClose}
            className="h-12 px-6 bg-ink text-paper rounded-btn font-sans text-[18px] font-bold"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  if (confirmingClose) {
    return (
      <div data-testid="bug-report-confirm-close" className="p-5 flex flex-col gap-4">
        <p className="font-sans text-base text-ink">{t('waiter.bugReport.discard.message')}</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            data-testid="bug-report-discard-cancel"
            onClick={() => setConfirmingClose(false)}
            className="h-12 px-4 rounded-btn font-sans text-[18px] font-bold underline text-ink"
          >
            {t('waiter.bugReport.discard.cancel')}
          </button>
          <button
            type="button"
            data-testid="bug-report-discard-confirm"
            onClick={onClose}
            className="h-12 px-6 bg-ink text-paper rounded-btn font-sans text-[18px] font-bold"
          >
            {t('waiter.bugReport.discard.confirm')}
          </button>
        </div>
      </div>
    );
  }

  const missing: Array<'severity' | 'description'> = [];
  if (!severity) missing.push('severity');
  if (description.trim().length < MIN_DESCRIPTION_LENGTH) missing.push('description');
  const canSubmit = missing.length === 0 && isOnline && !submitting;

  return (
    <div data-testid="bug-report-sheet" className="p-5 flex flex-col gap-4 min-h-0 overflow-y-auto">
      <div className="flex items-center justify-between gap-2 shrink-0">
        <h2 className="font-sans text-[18px] font-bold">{t('waiter.bugReport.title')}</h2>
        <button
          type="button"
          data-testid="bug-report-close"
          aria-label={t('common.close')}
          onClick={requestClose}
          className="-me-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
        >
          ×
        </button>
      </div>
      <p className="shrink-0 font-sans text-base text-ink-mute">{t('waiter.bugReport.intro')}</p>

      <div className="shrink-0 flex flex-col gap-2">
        <p id="bug-report-severity-label" className="font-sans text-base font-bold text-ink">
          {t('waiter.bugReport.severity.label')}
        </p>
        <div role="radiogroup" aria-labelledby="bug-report-severity-label" className="grid grid-cols-3 gap-2">
          {SEVERITIES.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={severity === value}
              data-testid={`bug-report-severity-${value}`}
              onClick={() => setSeverity(value)}
              className={`min-h-[56px] px-2 py-1.5 flex flex-col items-center justify-center gap-0.5 rounded-btn border-2 border-ink font-sans text-center ${
                severity === value ? 'bg-ink text-paper' : 'bg-paper text-ink'
              }`}
            >
              <span className="text-[16px] font-bold">{t(`waiter.bugReport.severity.${value}`)}</span>
              {value === 'blocking' && (
                <span className="text-base font-normal opacity-80">
                  {t('waiter.bugReport.severity.blockingHint')}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="shrink-0 flex flex-col gap-2">
        <label htmlFor="bug-report-description" className="font-sans text-base font-bold text-ink">
          {t('waiter.bugReport.descriptionLabel')}
        </label>
        <textarea
          id="bug-report-description"
          data-testid="bug-report-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('waiter.bugReport.descriptionPlaceholder')}
          rows={4}
          maxLength={MAX_DESCRIPTION_LENGTH}
          className="shrink-0 border-2 border-ink rounded-btn-sm px-3 py-2 text-base font-sans bg-paper text-ink resize-none"
        />
      </div>

      <div className="shrink-0 flex flex-col gap-2">
        {/* Pas d'attribut `capture` : il ouvrirait la caméra directement et
            masquerait la galerie. Sans lui, le système propose « Prendre une
            photo / Filmer » ET la galerie — là où vivent la capture d'écran
            et l'enregistrement d'écran, les meilleures preuves d'un bug. */}
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="bug-report-add-photo"
            disabled={images.length >= MAX_IMAGES}
            onClick={() => photoInputRef.current?.click()}
            className="h-12 px-4 flex items-center justify-center rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('waiter.bugReport.addPhoto')}
          </button>
          <input
            ref={photoInputRef}
            data-testid="bug-report-photo-input"
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => void handlePhotoChange(e)}
          />
          <button
            type="button"
            data-testid="bug-report-add-video"
            disabled={!!video}
            onClick={() => videoInputRef.current?.click()}
            className="h-12 px-4 flex items-center justify-center rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('waiter.bugReport.addVideo')}
          </button>
          <input
            ref={videoInputRef}
            data-testid="bug-report-video-input"
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => void handleVideoChange(e)}
          />
        </div>
        {(images.length > 0 || video) && (
          <div className="flex flex-wrap gap-3">
            {images.map((image, index) => (
              <div key={image.previewUrl} className="relative w-16 h-16">
                <div
                  data-testid={`bug-report-image-${index}`}
                  className="w-full h-full rounded-btn-sm overflow-hidden border-2 border-ink"
                >
                  <img src={image.previewUrl} alt="" className="w-full h-full object-cover" />
                </div>
                {/* review F1#2 — la zone de tap fait 44×44 (le puce visible
                    reste 24×24) : un `<span>` décoratif au centre d'un
                    `<button>` plus grand, ancré au coin plutôt que centré
                    sur la vignette elle-même. */}
                <button
                  type="button"
                  data-testid={`bug-report-remove-image-${index}`}
                  aria-label={t('waiter.bugReport.removeAttachment')}
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 w-11 h-11 flex items-center justify-center"
                >
                  <span className="w-6 h-6 rounded-full flex items-center justify-center bg-ink text-paper text-sm leading-none">
                    ×
                  </span>
                </button>
              </div>
            ))}
            {video && (
              <div className="relative w-16 h-16">
                <div
                  data-testid="bug-report-video-thumb"
                  className="w-full h-full rounded-btn-sm overflow-hidden border-2 border-ink flex flex-col items-center justify-center gap-0.5 bg-paper text-ink"
                >
                  <span aria-hidden="true" className="text-lg leading-none">
                    ▶
                  </span>
                  {videoDurationSeconds != null && (
                    <span className="text-sm font-bold font-sans">{Math.round(videoDurationSeconds)}s</span>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="bug-report-remove-video"
                  aria-label={t('waiter.bugReport.removeAttachment')}
                  onClick={removeVideo}
                  className="absolute -top-2 -right-2 w-11 h-11 flex items-center justify-center"
                >
                  <span className="w-6 h-6 rounded-full flex items-center justify-center bg-ink text-paper text-sm leading-none">
                    ×
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <details data-testid="bug-report-context-details" className="shrink-0">
        <summary className="cursor-pointer font-sans text-base text-ink-mute underline">
          {t('waiter.bugReport.techInfoNotice')}
        </summary>
        <pre
          data-testid="bug-report-context"
          className="mt-2 text-[13px] font-mono bg-cream border border-hairline p-2 overflow-x-auto whitespace-pre-wrap"
        >
          {JSON.stringify(collectContext(i18n.language), null, 2)}
        </pre>
      </details>

      {!isOnline && (
        <p data-testid="bug-report-offline-notice" role="status" className="shrink-0 font-sans text-base text-ink">
          {t('waiter.bugReport.offlineNotice')}
        </p>
      )}
      {isOnline && missing.length > 0 && !submitting && (
        <p data-testid="bug-report-missing" role="status" className="shrink-0 font-sans text-base text-ink-mute">
          {missing.map((field) => t(`waiter.bugReport.errors.${field}Required`)).join(' ')}
        </p>
      )}
      {fieldErrors.length > 0 && (
        <p
          data-testid="bug-report-field-error"
          role="alert"
          className={`shrink-0 ${ERROR_TEXT_CLASSES}`}
          style={ERROR_TEXT_STYLE}
        >
          {t(`waiter.bugReport.errors.${fieldErrors[0]}`)}
        </p>
      )}
      {serverError && (
        <p
          data-testid="bug-report-server-error"
          role="alert"
          className={`shrink-0 ${ERROR_TEXT_CLASSES}`}
          style={ERROR_TEXT_STYLE}
        >
          {t(serverErrorKey(serverError))}
        </p>
      )}
      {submitting && (
        <div
          data-testid="bug-report-progress"
          role="progressbar"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          className="shrink-0 h-2 w-full rounded-full border border-hairline bg-cream overflow-hidden"
        >
          <div className="h-full bg-ink" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}

      <div className="shrink-0 flex justify-end">
        <button
          type="button"
          data-testid="bug-report-submit"
          disabled={!canSubmit}
          aria-busy={submitting}
          onClick={() => void handleSubmit()}
          className="h-12 px-6 bg-ink text-paper rounded-btn font-sans text-[18px] font-bold disabled:opacity-50"
        >
          {t('waiter.bugReport.submit')}
        </button>
      </div>
    </div>
  );
}
